import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ReactNode } from 'react';
import { ThemeProvider } from './context/ThemeContext';

// Page components
import { LandingPage } from './pages/LandingPage';
import { Dashboard } from './pages/Dashboard';
import { LeadDetail } from './pages/LeadDetail';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminLogs } from './pages/AdminLogs';
import { AdminLeads } from './pages/AdminLeads';
import { AdminLeadDetail } from './pages/AdminLeadDetail';
import { AdminAccessCodes } from './pages/AdminAccessCodes';
import { AdminCampaigns } from './pages/AdminCampaigns';

/**
 * Protected route wrapper for user authentication.
 * Checks localStorage for 'rewagent_user' session.
 * Redirects to / if not authenticated.
 */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const stored = localStorage.getItem('rewagent_user');

  if (!stored) {
    return <Navigate to="/" replace />;
  }

  // Validate that the stored value is valid JSON
  try {
    JSON.parse(stored);
    return <>{children}</>;
  } catch {
    localStorage.removeItem('rewagent_user');
    return <Navigate to="/" replace />;
  }
}

/**
 * Protected route wrapper for admin authentication.
 * Checks localStorage for 'rewagent_admin' session.
 * Redirects to /admin if not authenticated.
 */
function AdminProtectedRoute({ children }: { children: ReactNode }) {
  const stored = localStorage.getItem('rewagent_admin');

  if (!stored) {
    return <Navigate to="/admin" replace />;
  }

  // Validate that the stored value is valid JSON
  try {
    JSON.parse(stored);
    return <>{children}</>;
  } catch {
    localStorage.removeItem('rewagent_admin');
    return <Navigate to="/admin" replace />;
  }
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />

          {/* Protected user routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lead/:id"
            element={
              <ProtectedRoute>
                <LeadDetail />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route path="/admin" element={<Navigate to="/" replace />} />
          <Route path="/admin/clients" element={<Navigate to="/admin/dashboard" replace />} />
          <Route
            path="/admin/dashboard"
            element={
              <AdminProtectedRoute>
                <AdminDashboard />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/leads"
            element={
              <AdminProtectedRoute>
                <AdminLeads />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/campaigns"
            element={
              <AdminProtectedRoute>
                <AdminCampaigns />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/leads/:id"
            element={
              <AdminProtectedRoute>
                <AdminLeadDetail />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/access-codes"
            element={
              <AdminProtectedRoute>
                <AdminAccessCodes />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/logs"
            element={
              <AdminProtectedRoute>
                <AdminLogs />
              </AdminProtectedRoute>
            }
          />

          {/* Catch-all redirect to landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
